'use client';
import React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import Input from '@ui/Input';
import Button from '@ui/Button';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { FormError } from '@ui/FormError';
import client from '@/clients/common/client';

import { cn } from '@/lib/utils';
import { forgotPasswordSchema } from '@/lib/validations/customer/authSchemas';

type FormData = z.infer<typeof forgotPasswordSchema>;

type Props = {
  url?: string;
};

export default function ForgotPasswordForm({ url }: Props) {
  const [successCard, setSuccessCard] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [backendErrors, setBackendErrors] = React.useState<Record<string, []>>(
    {}
  );

  const t = useTranslations();
  const RESET = `storefront.reset`;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit: SubmitHandler<FormData> = (data) => {
    setIsLoading(true);
    setBackendErrors({});
    setSuccessCard(false);
    client
      .post(url ?? '/v1/customer/password-reset/send-email', data)
      .then((res) => {
        // console.log(res.data);
        if (res.status === 200) {
          setSuccessCard(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        const errors = {
          ...error.response.data.errors,
          message: error.response.data.message,
        };
        // console.log(errors);
        setBackendErrors(errors);
      });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-[350px] flex-col items-center gap-y-[30px] rounded-[10px] bg-white px-[40px] py-[50px] shadow-[0_3px_6px_rgba(0,0,0,0.1)] md:w-[626px]"
    >
      <h1 className="text-h2 font-extrabold">{t(`${RESET}.header`)}</h1>
      {!!backendErrors.message && (
        <p className="flex w-full items-center justify-center rounded-[10px] bg-primary px-[30px] py-[15px] text-left text-lg text-white">
          {backendErrors.message}
        </p>
      )}
      {successCard && (
        <p className="flex w-full animate-jump-in items-center justify-center rounded-[10px] bg-[#75CFC833] p-[30px] text-left text-lg text-[#0A553A]">
          {t(`${RESET}.success`)}
        </p>
      )}
      <div className="flex w-full flex-col">
        <Input
          type="email"
          id="email"
          isError={Boolean(errors.email)}
          placeholder={t('email')}
          {...register('email', { required: true })}
        />
        {errors.email && errors.email?.message && (
          <FormError
            className="self-start"
            message={errors.email.message as string}
          />
        )}
        {backendErrors?.email?.length > 0
          ? backendErrors.email.map((e, i) => (
              <FormError key={i} message={e as string} />
            ))
          : null}
      </div>
      <Button
        rounded
        className={cn('min-w-[123px]')}
        type="submit"
        disabled={isLoading || !isValid}
      >
        {isLoading ? <LoadingSpinner /> : t(`${RESET}.send`)}
      </Button>
    </form>
  );
}
